<template>
  <div>
    <aside class="sidebar" :class="{ 'mobile-open': isMobileMenuOpen }">
      <div class="sidebar-header">
        <div class="logo-container">
          <BrandLogo :height="36" />
          <p class="admin-text">{{ $t('app.admin') }}</p>
        </div>
        <div class="mobile-close-btn" @click="$emit('closeMobileMenu')">
          <font-awesome-icon icon="times" />
        </div>
        <router-link v-if="auth.user && navLockMode !== 'orphan-admin'" to="/profile"
          class="sidebar-user-row desktop-only sidebar-user-row--link" @click="$emit('closeMobileMenu')">
          <img v-if="auth.user.avatar_url" :src="auth.user.avatar_url" alt="" class="sidebar-user-avatar" />
          <div v-else class="sidebar-user-avatar sidebar-user-avatar--placeholder">{{ sidebarUserInitials }}</div>
          <p class="user-name">{{ auth.user.first_name }} {{ auth.user.last_name }}</p>
        </router-link>
        <div v-else-if="auth.user" class="sidebar-user-row desktop-only">
          <img v-if="auth.user.avatar_url" :src="auth.user.avatar_url" alt="" class="sidebar-user-avatar" />
          <div v-else class="sidebar-user-avatar sidebar-user-avatar--placeholder">{{ sidebarUserInitials }}</div>
          <p class="user-name">{{ auth.user.first_name }} {{ auth.user.last_name }}</p>
        </div>
        <p class="version-info desktop-only">v1.0.0.0</p>
        <div v-if="isSuperAdmin" class="project-switcher desktop-only" :class="{
          'project-switcher--needs-project':
            projectContext.projectsLoaded && projectContext.projects.length === 0
        }">
          <p v-if="projectContext.projectsLoaded && projectContext.projects.length === 0"
            class="project-setup-sidebar-title">
            {{ $t('navigation.projectRequiredSidebarTitle') }}
          </p>
          <label class="project-label">{{ $t('navigation.project') }}</label>
          <select :value="projectContext.activeProjectId ?? ''" class="project-select" @change="onProjectChange">
            <option value="" disabled>
              {{
                projectContext.projects.length > 0
                  ? $t('navigation.selectProject')
                  : projectContext.projectsLoaded
                    ? $t('navigation.noProjects')
                    : $t('navigation.loadingProjects')
              }}
            </option>
            <option v-for="project in projectContext.projects" :key="project.uuid" :value="project.uuid">
              {{ project.name }}
            </option>
          </select>
          <p v-if="projectContext.projects.length === 0" class="project-empty-note">
            {{ $t('navigation.noProjectsHint') }}
          </p>
          <router-link to="/admins?tab=projects" class="project-manage-link" :class="{
            'project-manage-link--primary':
              projectContext.projectsLoaded && projectContext.projects.length === 0
          }" @click="$emit('closeMobileMenu')">
            {{ $t('navigation.manageProjects') }}
          </router-link>
        </div>
      </div>

      <nav class="sidebar-nav">
        <p v-if="navLockMode === 'superadmin'" class="onboarding-nav-hint">{{ $t('navigation.onboardingNavHint') }}</p>
        <p v-else-if="navLockMode === 'orphan-admin'" class="onboarding-nav-hint">{{
          $t('navigation.noProjectMembershipNavHint') }}</p>
        <template v-for="item in displayNavItems" :key="item.id">
          <router-link v-if="isNavItemClickable(item)" :to="navLinkTo(item)" class="nav-item"
            :class="{ 'nav-item--onboarding-focus': navLockMode === 'superadmin' && item.id === 'admins' }"
            active-class="active" @click="$emit('closeMobileMenu')">
            <font-awesome-icon :icon="item.icon" />
            <span>{{ $t(item.i18nKey) }}</span>
          </router-link>
          <span v-else class="nav-item nav-item--disabled" tabindex="-1" :title="navItemDisabledTitle">
            <font-awesome-icon :icon="item.icon" />
            <span>{{ $t(item.i18nKey) }}</span>
          </span>
</template>
      </nav>

      <div class="sidebar-footer">
        <LanguageSwitcher class="language-switcher-container" />
        <router-link v-if="navLockMode !== 'orphan-admin'" to="/profile" class="nav-item profile-link"
          active-class="active" @click="$emit('closeMobileMenu')">
          <font-awesome-icon icon="user-circle" />
          <span>{{ $t('profile.title') }}</span>
        </router-link>
        <span v-else class="nav-item profile-link nav-item--disabled" tabindex="-1" :title="navItemDisabledTitle">
          <font-awesome-icon icon="user-circle" />
          <span>{{ $t('profile.title') }}</span>
        </span>
        <button @click="handleLogout" class="logout-button">
          <font-awesome-icon icon="fa-power-off" />
          <span>{{ $t('navigation.logout') }}</span>
        </button>
      </div>
    </aside>

    <div class="mobile-overlay" :class="{ 'active': isMobileMenuOpen }" @click="$emit('closeMobileMenu')"></div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/stores/auth'
import { useProjectContextStore } from '@/stores/projectContext'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'
import LanguageSwitcher from '../language/LanguageSwitcher.vue'
import BrandLogo from '../BrandLogo.vue'

const { t } = useI18n()

interface NavItem {
  id: string
  to: RouteLocationRaw
  icon: string
  i18nKey: string
  minRole: 'all' | 'admin' | 'superadmin'
}

const props = defineProps<{
  isMobileMenuOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'closeMobileMenu'): void
}>()

const auth = useAuthStore()
const projectContext = useProjectContextStore()

const isSuperAdmin = computed(() => {
  return auth.user?.role === 'superadmin'
})

const isAdmin = computed(() => {
  return auth.user?.role === 'admin' || auth.user?.role === 'superadmin'
})

const sidebarUserInitials = computed(() => {
  if (!auth.user) return '?'
  const a = (auth.user.first_name || '').trim().charAt(0)
  const b = (auth.user.last_name || '').trim().charAt(0)
  return (a + b).toUpperCase() || '?'
})

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', to: '/dashboard', icon: 'fa-gauge-high', i18nKey: 'navigation.dashboard', minRole: 'all' },
  {
    id: 'establishments',
    to: '/establishments',
    icon: 'fa-building',
    i18nKey: 'navigation.establishments',
    minRole: 'all'
  },
  {
    id: 'locations',
    to: '/locations',
    icon: 'fa-map-marker-alt',
    i18nKey: 'navigation.locations',
    minRole: 'all'
  },

  { id: 'menus', to: '/menus', icon: 'fa-rectangle-list', i18nKey: 'navigation.menus', minRole: 'all' },
  { id: 'news', to: '/news', icon: 'fa-newspaper', i18nKey: 'navigation.news', minRole: 'all' },
  { id: 'customers', to: '/customers', icon: 'fa-users', i18nKey: 'navigation.customers', minRole: 'admin' },
  { id: 'promo', to: '/promo-codes', icon: 'percent', i18nKey: 'navigation.promo', minRole: 'admin' },
  { id: 'membership', to: '/membership-programs', icon: 'id-badge', i18nKey: 'navigation.membership', minRole: 'admin' },
  { id: 'rewards', to: { name: 'rewards' }, icon: 'gift', i18nKey: 'navigation.rewards', minRole: 'admin' },
  { id: 'admins', to: '/admins', icon: 'fa-users-gear', i18nKey: 'navigation.admins', minRole: 'superadmin' },
  {
    id: 'office-admins',
    to: '/office-admins',
    icon: 'user-tie',
    i18nKey: 'navigation.employees',
    minRole: 'admin'
  }
]

function navItemsFiltered(): NavItem[] {
  return NAV_ITEMS.filter((item) => {
    if (item.minRole === 'superadmin' && !isSuperAdmin.value) return false
    if (item.minRole === 'admin' && !isAdmin.value) return false
    if (
      auth.user?.role === 'admin' &&
      (projectContext.activeProjectMembershipRole === 'contributor' ||
        projectContext.activeProjectMembershipRole === 'viewer') &&
      item.id === 'customers'
    ) {
      return false
    }
    return true
  })
}

type NavLockMode = 'none' | 'superadmin' | 'orphan-admin'

const navLockMode = computed((): NavLockMode => {
  if (!projectContext.projectsLoaded || projectContext.projects.length > 0) return 'none'
  if (isSuperAdmin.value) return 'superadmin'
  if (auth.user?.role === 'admin') return 'orphan-admin'
  return 'none'
})

const displayNavItems = computed(() => {
  const list = [...navItemsFiltered()]
  if (navLockMode.value !== 'superadmin') return list
  const idx = list.findIndex((i) => i.id === 'admins')
  if (idx <= 0) return list
  const [adminsEntry] = list.splice(idx, 1)
  return [adminsEntry, ...list]
})

const navItemDisabledTitle = computed(() =>
  navLockMode.value === 'orphan-admin' ? '' : t('navigation.navLockedUntilProject')
)

function isNavItemClickable(item: NavItem): boolean {
  if (navLockMode.value === 'none') return true
  if (navLockMode.value === 'orphan-admin') return false
  return item.id === 'admins'
}

function navLinkTo(item: NavItem): RouteLocationRaw {
  if (navLockMode.value === 'superadmin' && item.id === 'admins') {
    return { path: '/admins', query: { tab: 'projects' } }
  }
  return item.to
}

const getRoleName = () => {
  if (!auth.user) return ''

  switch (auth.user.role) {
    case 'superadmin':
      return 'Super Admin'
    case 'admin':
      return 'Administrator'
    default:
      return auth.user.role
  }
}

const onProjectChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  projectContext.setActiveProject(target.value || null)
}

const handleLogout = async () => {
  await auth.logout()
  emit('closeMobileMenu')
}
</script>

<style scoped>
.mobile-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 28;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  backdrop-filter: blur(3px);
}

.mobile-overlay.active {
  opacity: 1;
  visibility: visible;
}

.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: var(--backoffice-sidebar-width);
  background: linear-gradient(150deg, #20232a 0%, #2a2e38 40%, #2d2520 85%, #3d2e1e 100%);
  color: white;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  z-index: 30;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
}

.sidebar::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background:
    linear-gradient(160deg, transparent 0%, rgba(216, 103, 12, 0.03) 25%, transparent 50%),
    radial-gradient(circle at 15% 15%, rgba(216, 103, 12, 0.08) 0%, transparent 30%);
  pointer-events: none;
  z-index: -1;
}

.logo-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
}

.admin-text {
  margin: 0;
  font-size: 0.7rem;
  color: #a0a0a0;
  font-weight: normal;
  letter-spacing: 0.02em;
}

.sidebar-header {
  position: relative;
  padding: 0.75rem 0;
  border-bottom: 1px solid #333;
  margin-bottom: 1rem;
}

.mobile-close-btn {
  display: none;
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  color: #e0e0e0;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1.1rem;
  background-color: rgba(255, 255, 255, 0.1);
}

.mobile-close-btn:hover,
.mobile-close-btn:active {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.sidebar-header .sidebar-user-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 0.5rem;
}

.sidebar-user-row--link {
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  border-radius: 8px;
  margin-left: -0.35rem;
  margin-right: -0.35rem;
  padding: 0.35rem 0.35rem;
  transition: background 0.15s ease;
}

.sidebar-user-row--link:hover {
  background: rgba(255, 255, 255, 0.07);
}

.sidebar-user-row--link:focus-visible {
  outline: 2px solid rgba(216, 103, 12, 0.85);
  outline-offset: 2px;
}

.sidebar-header .sidebar-user-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.18);
}

.sidebar-header .sidebar-user-avatar--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}

.sidebar-header .sidebar-user-row .user-name {
  margin: 0;
  font-size: 0.8rem;
  color: #a0a0a0;
  font-weight: normal;
  line-height: 1.25;
  min-width: 0;
}

.sidebar-header .version-info {
  margin: 0.25rem 0 0 0;
  font-size: 0.7rem;
  color: #666;
  font-weight: normal;
}

.project-switcher {
  margin-top: 0.75rem;
}

.project-switcher--needs-project {
  padding: 0.65rem 0.55rem 0.75rem;
  margin-left: -0.15rem;
  margin-right: -0.15rem;
  border-radius: 8px;
  border: 1px solid #f97316;
  background: rgba(249, 115, 22, 0.14);
}

.project-setup-sidebar-title {
  margin: 0 0 0.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #fdba74;
  line-height: 1.35;
}

.project-manage-link--primary {
  font-weight: 700;
  border-width: 2px;
  box-shadow: 0 0 0 1px rgba(253, 186, 116, 0.35);
}

.project-label {
  display: block;
  font-size: 0.7rem;
  margin-bottom: 0.25rem;
  color: #a0a0a0;
}

.project-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 100%;
  border-radius: 0.375rem;
  border: 1px solid #3b3f48;
  background: #232730;
  background-image: linear-gradient(45deg, transparent 50%, #c3c9d2 50%), linear-gradient(135deg, #c3c9d2 50%, transparent 50%);
  background-position: calc(100% - 14px) 50%, calc(100% - 9px) 50%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  color: #fff;
  padding: 0.45rem 2rem 0.45rem 0.55rem;
  font-size: 0.75rem;
}

.project-manage-link {
  display: block;
  text-align: center;
  text-decoration: none;
  width: 100%;
  margin-top: 0.55rem;
  border: 1px solid #d8670c;
  background: linear-gradient(90deg, #d8670c 0%, #c05a0a 100%);
  color: #fff;
  border-radius: 0.375rem;
  padding: 0.42rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(216, 103, 12, 0.25);
}

.project-manage-link:hover {
  filter: brightness(1.05);
}

.project-empty-note {
  margin: 0.4rem 0 0 0;
  color: #b8bec8;
  font-size: 0.72rem;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  overflow-y: auto;
}

.onboarding-nav-hint {
  margin: 0 0 0.35rem;
  padding: 0.5rem 0.65rem;
  font-size: 0.72rem;
  line-height: 1.4;
  color: #fdba74;
  background: rgba(249, 115, 22, 0.12);
  border-radius: 6px;
  border: 1px solid rgba(249, 115, 22, 0.35);
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  min-height: 44px;
  width: 100%;
  box-sizing: border-box;
  color: #e0e0e0;
  text-decoration: none;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.nav-item--onboarding-focus {
  border: 1px solid #fb923c;
  background: rgba(251, 146, 60, 0.12);
}

.nav-item--onboarding-focus:hover {
  background: rgba(251, 146, 60, 0.2);
}

.nav-item--disabled {
  opacity: 0.42;
  cursor: not-allowed;
  pointer-events: none;
}

.nav-item--disabled:hover {
  background-color: transparent;
}

.nav-item:hover {
  background-color: #333;
}

.nav-item.active {
  background: linear-gradient(90deg, #D8670C 0%, #c05a0a 100%);
  color: white;
  box-shadow: 0 2px 5px rgba(216, 103, 12, 0.3);
  position: relative;
  overflow: hidden;
}

.nav-item.active::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0) 100%);
  animation: shine 3s infinite;
}

@keyframes shine {
  0% {
    left: -100%;
  }

  20% {
    left: 100%;
  }

  100% {
    left: 100%;
  }
}

.sidebar-footer {
  border-top: 1px solid #333;
  padding-top: 1rem;
  margin-top: auto;
}

.profile-link {
  margin-bottom: 0.75rem;
}

.language-switcher-container {
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
}

.logout-button {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: transparent;
  border: 1px solid #333;
  color: #e0e0e0;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.logout-button:hover {
  background: #D8670C;
  border-color: #D8670C;
}

@media (max-width: 768px) {
  .mobile-overlay {
    display: block;
  }

  .sidebar {
    left: auto;
    right: -100%;
    transform: translateX(0);
    width: 100%;
    height: 100vh;
    padding: 1.5rem;
    box-shadow: none;
  }

  .sidebar.mobile-open {
    transform: translateX(0);
    left: auto;
    right: 0;
    box-shadow: -2px 0 20px rgba(0, 0, 0, 0.5);
  }

  .mobile-close-btn {
    display: flex;
    top: 1rem;
    right: 1rem;
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
    z-index: 40;
  }

  .desktop-only {
    display: block;
  }

  .sidebar-nav {
    padding-top: 1rem;
    padding-bottom: 1rem;
    overflow-y: auto;
  }

  .sidebar-footer {
    padding-top: 1rem;
    margin-top: auto;
  }
}
</style>